import React from 'react';
import { Box, Icon, IconNameT, MediaGalleryViewer, prettyBytes, Text, VStack } from '@cad-arc';
import { CombinedMediaItem } from './types';
import { useAttachmentSizeLimits } from './hooks/use-attachment-size-limits';
import 'vidstack/bundle';
import { canPreviewAttachment } from './helpers';

interface CustomMediaItemViewerProps {
    combinedItems: CombinedMediaItem[];
    selectedId: string | null;
}

const getMediaTypeIcon = (type: CombinedMediaItem['type']): IconNameT => {
    switch (type) {
        case 'pdf': {
            return 'PDFDocument';
        }
        case 'image': {
            return 'Image';
        }
        case 'video': {
            return 'VideoFile';
        }
        case 'audio': {
            return 'AudioFile';
        }
        default: {
            return 'Document';
        }
    }
};

const getMediaTypeDisplayName = (type: CombinedMediaItem['type']): string => {
    switch (type) {
        case 'pdf': {
            return 'PDF';
        }
        case 'image': {
            return 'Image';
        }
        case 'video': {
            return 'Video';
        }
        case 'audio': {
            return 'Audio';
        }
        default: {
            return 'File';
        }
    }
};

const MediaExceedsPreviewLimitMessage = ({
    fileSizeBytes,
    maxPreviewSizeBytes,
    mediaType,
}: {
    fileSizeBytes: number | undefined;
    maxPreviewSizeBytes?: number | null;
    mediaType: CombinedMediaItem['type'];
}) => {
    return (
        <Box width="100%" height="100%" display="flex" alignItems="center" justifyContent="center">
            <VStack spacing="md" alignItems="center">
                <Icon icon={getMediaTypeIcon(mediaType)} size="lg" />
                <Text fontSize="lg" fontWeight="semibold">
                    {getMediaTypeDisplayName(mediaType)} Exceeds Preview Limit
                </Text>
                {!!maxPreviewSizeBytes && (
                    <>
                        {fileSizeBytes && (
                            <Text color="text.secondary">
                                File size: {prettyBytes(fileSizeBytes)}
                            </Text>
                        )}
                        <Text color="text.secondary">
                            Maximum preview size: {prettyBytes(maxPreviewSizeBytes)}
                        </Text>
                    </>
                )}
            </VStack>
        </Box>
    );
};

export function CustomMediaItemViewer({
    combinedItems,
    selectedId,
}: CustomMediaItemViewerProps): React.ReactElement {
    const sizeLimits = useAttachmentSizeLimits();
    const currentItem = combinedItems.find((item) => item.id === selectedId);
    if (!currentItem) {
        return (
            <Box
                position="relative"
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                No Attachment Selected
            </Box>
        );
    }

    return (
        <Box
            width="100%"
            height="100%"
            display="flex"
            flexDirection="column"
            sx={{
                // configure video/audio player to fill available space
                '.arc-media-player': {
                    width: '100%',
                    height: '100%',
                    aspectRatio: 'auto',
                },
            }}
        >
            <MediaGalleryViewer
                aspectRatio="auto"
                showArrows={true}
                showCounter={false}
                renderPreview={(item) => {
                    const currentItem = item as CombinedMediaItem;
                    // check if item is too large to preview
                    if (
                        !canPreviewAttachment(
                            currentItem.type,
                            currentItem.fileSizeBytes,
                            sizeLimits
                        )
                    ) {
                        return (
                            <MediaExceedsPreviewLimitMessage
                                fileSizeBytes={currentItem?.fileSizeBytes}
                                maxPreviewSizeBytes={sizeLimits.getMaxPreviewSizeForItem(
                                    currentItem
                                )}
                                mediaType={currentItem?.type}
                            />
                        );
                    }

                    // return null to fall back to Arc's default preview
                    return null;
                }}
            />
        </Box>
    );
}
